import { useState } from 'react'
import type { QCRemark } from '@/api/types'
import { useUser } from '@/auth/useAuth'
import { qcSeverityLabel, qcStatusLabel } from '@/i18n/labels'
import { useDepartmentMap } from '@/features/refs/queries'
import { Button, Card, EmptyState, ErrorState, LoadingState, cn } from '@/ui'
import { QCCreateSheet } from './QCCreateSheet'
import { QCRemarkSheet } from './QCRemarkSheet'
import { useQCRemarks } from './queries'

const severityTone: Record<QCRemark['severity'], string> = {
  MINOR: 'text-amber-700',
  MAJOR: 'text-orange-700',
  CRITICAL: 'text-risk-red',
}

export function QCTab({ orderId }: { orderId: string }) {
  const user = useUser()
  const { data, isLoading, isError, error, refetch } = useQCRemarks(orderId)
  const deptMap = useDepartmentMap()
  const [createOpen, setCreateOpen] = useState(false)
  const [selected, setSelected] = useState<QCRemark | null>(null)

  const inQcDept = user.department_ids.some((id) => deptMap.get(id)?.is_quality_control)
  const canCreate =
    user.role === 'ADMIN' || user.role === 'PRODUCTION_MANAGER' || user.role === 'OWNER' || inQcDept

  return (
    <div>
      {canCreate && (
        <div className="mb-3 flex justify-end">
          <Button size="md" onClick={() => setCreateOpen(true)}>
            + Замечание
          </Button>
        </div>
      )}

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : !data || data.items.length === 0 ? (
        <EmptyState title="Замечаний нет" />
      ) : (
        <div className="space-y-2">
          {data.items.map((r) => (
            <Card key={r.id}>
              <button
                className="w-full px-4 py-3 text-left active:bg-black/5"
                onClick={() => setSelected(r)}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-medium text-tg-text">{r.title}</span>
                  <span className="text-xs text-tg-hint">{qcStatusLabel[r.status]}</span>
                </div>
                <div className="mt-0.5 text-xs">
                  <span className={cn('font-medium', severityTone[r.severity])}>
                    {qcSeverityLabel[r.severity]}
                  </span>
                  {' · '}
                  {deptMap.get(r.target_department_id)?.name ?? 'отдел'}
                </div>
              </button>
            </Card>
          ))}
        </div>
      )}

      <QCCreateSheet orderId={orderId} open={createOpen} onClose={() => setCreateOpen(false)} />
      {selected && (
        <QCRemarkSheet
          orderId={orderId}
          remark={selected}
          open
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  )
}
